import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ClipboardList, UserCheck } from 'lucide-react';
import {
  apiMessage,
  createTask,
  getRoutingOverview,
  getTaskReference,
  listEmployees,
} from '../services/serviceInboxApi';
import { catLabel } from './categories';

/**
 * ManualTask — create a ServiceTitan task for one message by hand.
 *
 * Only for a matched customer (2026-09-26): the one a person marked as the
 * match (`stored.confirmed`), else the one the lookup settled on. Nothing is
 * picked here; to use someone else, mark them as the match in the Customer
 * section first. The service refuses any other customer. RNC and an office
 * without task routing are shown but cannot take a task.
 *
 * Who the task goes to comes from Task Routing for the picked customer's office
 * and the chosen category. Only where routing names nobody (or a contact not yet
 * linked to an employee) does the form offer an employee picker for that role.
 *
 * A ServiceTitan task cannot be deleted, so creating one takes a second click
 * that says so, and the service allows one task per email.
 */

// The email's matched customer: the one a person marked, else the one the
// lookup settled on, or null when there is neither.
export const matchedCustomer = (stored) => {
  const c = stored.confirmed;
  if (c) {
    return {
      tenant_id: c.tenant_id,
      customer_id: c.customer_id,
      name: c.name,
      office: c.office,
      is_rnc: c.is_rnc,
      address: c.location,
      source: 'confirmed',
    };
  }
  const m = stored.customer;
  if (m?.name && m.customer_id != null && m.tenant_id) {
    return {
      tenant_id: m.tenant_id,
      customer_id: m.customer_id,
      name: m.name,
      office: m.office,
      is_rnc: m.is_rnc,
      address: m.location,
      source: 'lookup',
    };
  }
  return null;
};

const personLabel = (p) =>
  p ? [p.name || p.email, p.employee_id != null && `#${p.employee_id}`].filter(Boolean).join(' ') : '';

// One role's person: routing's, or a picker when routing has no employee.
const PersonField = ({ label, routed, employees, value, onChange }) => {
  if (routed?.employee_id != null) {
    return (
      <div className="text-sm">
        <span className="text-gray-500">{label}: </span>
        <span className="text-gray-900">{personLabel(routed)}</span>
        <span className="ml-1 text-xs text-gray-500">(Task Routing)</span>
      </div>
    );
  }
  return (
    <label className="block text-sm">
      <span className="text-gray-500">
        {label}
        {routed ? ' (routed contact has no ServiceTitan employee yet)' : ' (not routed)'}
      </span>
      {employees.list.length > 0 ? (
        <select
          className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Pick an employee…</option>
          {employees.list.map((e) => (
            <option key={e.employee_id} value={String(e.employee_id)}>
              {e.name || e.email || `#${e.employee_id}`}
            </option>
          ))}
        </select>
      ) : (
        <input
          className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-sm"
          inputMode="numeric"
          placeholder={employees.loading ? 'Loading employees…' : 'ServiceTitan employee ID'}
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
        />
      )}
    </label>
  );
};

// `autoOpen`: start with the form open (the card's quick action), not the button.
const ManualTask = ({ stored, onCreated, autoOpen = false }) => {
  const [open, setOpen] = useState(autoOpen);
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState(null);
  const [category, setCategory] = useState(stored.category || '');
  const [summary, setSummary] = useState(stored.summary || '');
  const [employees, setEmployees] = useState({ loading: false, list: [] });
  // The tenant's task types, business units and priorities, and the form's
  // choices among them (2026-10-15). Each starts on the automatic writer's
  // default and is sent only when it differs, so an untouched form makes the
  // same task the poller would.
  const [reference, setReference] = useState({ loading: false, data: null, error: null });
  const [taskType, setTaskType] = useState(''); // id, as a string
  const [typeTouched, setTypeTouched] = useState(false);
  const [priority, setPriority] = useState('Normal');
  const [businessUnit, setBusinessUnit] = useState(''); // id, as a string
  const [due, setDue] = useState(''); // yyyy-mm-dd, local
  const [assignee, setAssignee] = useState('');
  const [reporter, setReporter] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const picked = useMemo(() => matchedCustomer(stored), [stored]);

  // The routing table, once the form opens: tenants, categories and people.
  useEffect(() => {
    if (!open || overview) return;
    getRoutingOverview()
      .then(setOverview)
      .catch((err) => setOverviewError(apiMessage(err, 'Could not load Task Routing.')));
  }, [open, overview]);

  // The reference lists, once the form opens for a known tenant.
  const tenantForRef = picked?.tenant_id || '';
  useEffect(() => {
    if (!open || !tenantForRef) return undefined;
    let live = true;
    setReference({ loading: true, data: null, error: null });
    getTaskReference(tenantForRef)
      .then((data) => live && setReference({ loading: false, data, error: null }))
      .catch((err) =>
        live &&
        setReference({
          loading: false,
          data: null,
          error: apiMessage(err, 'Could not load the task types; the defaults apply.'),
        })
      );
    return () => {
      live = false;
    };
  }, [open, tenantForRef]);

  const refData = reference.data;
  const idOf = (rows, name) =>
    rows?.find((r) => r.name.toLowerCase() === (name || '').toLowerCase())?.id;
  const defaultTypeId = idOf(refData?.task_types, refData?.defaults?.task_types?.[category]);
  const defaultUnitId = idOf(refData?.business_units, refData?.defaults?.business_unit);
  // The category's own task type, until the person picks one.
  useEffect(() => {
    if (!typeTouched) setTaskType(defaultTypeId != null ? String(defaultTypeId) : '');
  }, [defaultTypeId, typeTouched]);
  useEffect(() => {
    if (defaultUnitId != null && !businessUnit) setBusinessUnit(String(defaultUnitId));
  }, [defaultUnitId]); // eslint-disable-line

  const routable = useMemo(
    () => new Map((overview?.tenants || []).map((t) => [t.tenant_id, t])),
    [overview]
  );
  const disabledReason = (c) => {
    if (c.is_rnc) return 'RNC · out of scope';
    if (overview && !routable.has(c.tenant_id)) return 'no task routing for this office';
    return null;
  };

  const tenantId = picked?.tenant_id;
  const route = (overview?.routes || []).find(
    (r) => r.tenant_id === tenantId && r.category === category
  );
  const reportedBy = (overview?.reporters || []).find((r) => r.tenant_id === tenantId);
  const needsPicker = !!tenantId && (route?.employee_id == null || reportedBy?.employee_id == null);

  // Employees for the picked office, only when a role has to be picked by hand.
  useEffect(() => {
    if (!needsPicker) return undefined;
    let live = true;
    setEmployees({ loading: true, list: [] });
    listEmployees(tenantId)
      .then((list) => live && setEmployees({ loading: false, list }))
      .catch(() => live && setEmployees({ loading: false, list: [] }));
    return () => {
      live = false;
    };
  }, [needsPicker, tenantId]);

  // A different office means different people: drop a hand pick.
  useEffect(() => {
    setAssignee('');
    setReporter('');
    setConfirming(false);
  }, [tenantId, category]);

  const assigneeId = route?.employee_id ?? (assignee ? Number(assignee) : null);
  const reporterId = reportedBy?.employee_id ?? (reporter ? Number(reporter) : null);
  const blocked = picked ? disabledReason(picked) : 'no matched customer';
  const ready =
    !!picked &&
    !blocked &&
    !!category &&
    summary.trim().length > 0 &&
    assigneeId != null &&
    reporterId != null;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const typeId = taskType ? Number(taskType) : null;
      const unitId = businessUnit ? Number(businessUnit) : null;
      const task = await createTask({
        email_id: stored.email_id,
        tenant_id: picked.tenant_id,
        customer_id: picked.customer_id,
        category,
        summary: summary.trim(),
        ...(route?.employee_id == null && { assigned_to_employee_id: assigneeId }),
        ...(reportedBy?.employee_id == null && { reported_by_employee_id: reporterId }),
        // Only what differs from the automatic writer's own choices.
        ...(typeId != null && typeId !== defaultTypeId && { task_type_id: typeId }),
        ...(priority !== 'Normal' && { priority: priority.toLowerCase() }),
        ...(unitId != null && unitId !== defaultUnitId && { business_unit_id: unitId }),
        // The chosen day at 5 pm local, as ServiceTitan shows due dates by day.
        ...(due && { complete_by: new Date(`${due}T17:00:00`).toISOString() }),
      });
      onCreated(task);
      if (task.status === 'created') setOpen(false);
      else setError(task.reason || 'ServiceTitan did not return a task.');
    } catch (err) {
      setError(apiMessage(err, 'Could not create the task.'));
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
      >
        <ClipboardList className="mr-1.5 h-4 w-4" />
        Create task…
      </button>
    );
  }

  // Opened from the card's quick action (`autoOpen`), the panel around the
  // form carries the title and the ×, so the form draws neither.
  return (
    <div className={autoOpen ? 'space-y-3' : 'mt-2 space-y-3 rounded-md border border-gray-200 bg-white p-3'}>
      {!autoOpen && (
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900">New ServiceTitan task</div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-xs text-gray-500 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
      )}
      {overviewError && <div className="text-sm text-red-700">{overviewError}</div>}

      {/* Customer: fixed, the email's matched customer */}
      <div>
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Customer</div>
        {picked && (
          <div className="rounded border border-gray-200 px-3 py-2 text-sm">
            <div className="flex flex-wrap items-center gap-x-2">
              <UserCheck className="h-4 w-4 shrink-0 text-green-700" />
              <span className="font-semibold text-gray-900">{picked.name || '(no name)'}</span>
              <span className="tabular-nums text-gray-500">ST # {picked.customer_id}</span>
              {blocked && (
                <span className="rounded bg-amber-50 px-1.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                  {blocked}
                </span>
              )}
            </div>
            {picked.office && <div className="text-gray-600">{picked.office}</div>}
            {picked.address && <div className="text-gray-600">{picked.address}</div>}
            <div className="mt-0.5 text-xs text-gray-500">
              {picked.source === 'confirmed' ? 'Marked as the match' : 'Matched by the lookup'}
            </div>
          </div>
        )}
      </div>

      {/* Category and people */}
      <div className="space-y-2">
        <label className="block text-sm">
          <span className="text-gray-500">Category</span>
          <select
            className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-sm"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {!category && <option value="">Pick a category…</option>}
            {(overview?.categories || (category ? [category] : [])).map((c) => (
              <option key={c} value={c}>
                {catLabel(c)}
              </option>
            ))}
          </select>
        </label>
        {picked && overview && (
          <>
            <PersonField
              label="Assigned to"
              routed={route}
              employees={employees}
              value={assignee}
              onChange={setAssignee}
            />
            <PersonField
              label="Reported by"
              routed={reportedBy}
              employees={employees}
              value={reporter}
              onChange={setReporter}
            />
          </>
        )}
      </div>

      {/* Task type, priority, business unit and due date: ServiceTitan's own
          fields, on the automatic writer's defaults until changed. */}
      {reference.error && <div className="text-xs text-amber-700">{reference.error}</div>}
      {refData && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-gray-500">Task type</span>
            <select
              aria-label="Task type"
              className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-sm"
              value={taskType}
              onChange={(e) => {
                setTypeTouched(true);
                setTaskType(e.target.value);
              }}
            >
              {!taskType && <option value="">Pick a task type…</option>}
              {refData.task_types.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-gray-500">Priority</span>
            <select
              aria-label="Priority"
              className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-sm"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {(refData.priorities || ['Low', 'Normal', 'High', 'Urgent']).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-gray-500">Business unit</span>
            <select
              aria-label="Business unit"
              className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-sm"
              value={businessUnit}
              onChange={(e) => setBusinessUnit(e.target.value)}
            >
              {!businessUnit && <option value="">Pick a business unit…</option>}
              {refData.business_units.map((u) => (
                <option key={u.id} value={String(u.id)}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-gray-500">Due</span>
            <input
              type="date"
              aria-label="Due"
              className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-sm"
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
          </label>
        </div>
      )}
      {reference.loading && <div className="text-xs text-gray-500">Loading task types…</div>}

      {/* Summary */}
      <label className="block text-sm">
        <span className="text-gray-500">Summary</span>
        <textarea
          className="mt-0.5 block w-full rounded border border-gray-300 px-2 py-1 text-sm"
          rows={3}
          maxLength={2000}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
        />
      </label>

      {error && <div className="text-sm text-red-700">{error}</div>}

      {confirming ? (
        <div className="rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
          <div className="flex items-start gap-1.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Create a {catLabel(category)} task for <strong>{picked.name}</strong> (ST #{' '}
              {picked.customer_id}, {picked.office})? A ServiceTitan task cannot be deleted.
            </span>
          </div>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating…' : 'Create task'}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={saving}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={!ready}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Review and create…
        </button>
      )}
    </div>
  );
};

export default ManualTask;
